/**
 * Plays scroll reveal animations when `[data-scroll-reveal]` elements enter the viewport.
 *
 * CSS runs each reveal on render, so content stays visible if this module never loads.
 * Elements that start off screen are held in the hidden state until they intersect.
 * Add `data-scroll-reveal-replay` to hide the element again whenever it leaves the viewport.
 */

const SELECTOR = '[data-scroll-reveal]';
const STATE_ATTRIBUTE = 'data-scroll-reveal-state';

/** @type {WeakSet<Element>} */
const seen = new WeakSet();

const observer = new IntersectionObserver(handleIntersection, { rootMargin: '0px 0px -10% 0px' });

/**
 * @param {IntersectionObserverEntry[]} entries
 */
function handleIntersection(entries) {
  for (const { target, isIntersecting, boundingClientRect } of entries) {
    const replay = target.hasAttribute('data-scroll-reveal-replay');
    const firstEntry = !seen.has(target);
    seen.add(target);

    // Elements on screen at first observation are already playing their reveal; leave them be.
    const onScreen = boundingClientRect.top < window.innerHeight && boundingClientRect.bottom > 0;

    if (isIntersecting || (firstEntry && onScreen)) {
      target.removeAttribute(STATE_ATTRIBUTE);
      if (!replay) observer.unobserve(target);
    } else if (firstEntry || replay) {
      target.setAttribute(STATE_ATTRIBUTE, 'hidden');
    }
  }
}

/**
 * @param {Node} node
 * @param {'observe' | 'unobserve'} method
 */
function updateObservation(node, method) {
  if (!(node instanceof Element)) return;

  if (node.matches(SELECTOR)) observer[method](node);
  for (const element of node.querySelectorAll(SELECTOR)) observer[method](element);
}

updateObservation(document.documentElement, 'observe');

// Section rendering, the theme editor, and deferred content insert reveal elements after load.
new MutationObserver((mutations) => {
  for (const { addedNodes, removedNodes } of mutations) {
    for (const node of removedNodes) updateObservation(node, 'unobserve');
    for (const node of addedNodes) updateObservation(node, 'observe');
  }
}).observe(document.documentElement, { childList: true, subtree: true });
